import React from 'react';
import { useHelp } from '../../../context/HelpContext';
import '../../../assets/scss/views/Help.scss';

const ViewBackendWhatsappManager = () => {
  const { ui: { currentFile } } = useHelp();

  return (
    <div className="help-documentation-view">
      <div className="help-documentation-header">
        <h1>whatsappManager.js - Gestor de WhatsApp y WebSocket</h1>
        <p className="help-documentation-subtitle">
          Maneja las conexiones de WhatsApp y la comunicación en tiempo real via WebSocket
        </p>
      </div>

      <div className="help-documentation-content">
        <section className="help-documentation-section">
          <h2>Descripción General</h2>
          <p>
            El archivo <code>whatsappManager.js</code> es responsable de gestionar las conexiones 
            de WhatsApp Web y proporcionar comunicación en tiempo real a través de WebSocket. 
            Combina la funcionalidad de cliente WhatsApp con un servidor Socket.IO.
          </p>
        </section>

        <section className="help-documentation-section">
          <h2>Clases Principales</h2>
          
          <div className="help-documentation-code-block">
            <h3>SocketService</h3>
            <p>Gestiona las conexiones WebSocket y los eventos de comunicación.</p>
            <ul>
              <li><strong>setupRedisAdapter():</strong> Configura el adaptador Redis para escalabilidad</li>
              <li><strong>setupEventHandlers():</strong> Configura los manejadores de eventos</li>
              <li><strong>handleWhatsAppSocket():</strong> Maneja eventos específicos de WhatsApp</li>
              <li><strong>handleChatSocket():</strong> Maneja eventos de chat general</li>
            </ul>
          </div>

          <div className="help-documentation-code-block">
            <h3>ServerManager</h3>
            <p>Gestiona el servidor HTTP y la inicialización del sistema.</p>
            <ul>
              <li><strong>initialize():</strong> Inicializa el servidor y servicios</li>
              <li><strong>shutdown():</strong> Cierra el servidor de forma segura</li>
              <li><strong>getWhatsAppStatus():</strong> Obtiene el estado actual de WhatsApp</li>
            </ul>
          </div>
        </section>

        <section className="help-documentation-section">
          <h2>Eventos WebSocket</h2>
          
          <div className="help-documentation-code-block">
            <h3>Eventos de WhatsApp</h3>
            <ul>
              <li><code>request-qr</code> - Solicita código QR para conexión</li>
              <li><code>force-logout</code> - Fuerza el cierre de sesión</li>
              <li><code>request-chats</code> - Solicita lista de chats</li>
              <li><code>request-chat-messages</code> - Solicita mensajes de un chat</li>
              <li><code>send-message</code> - Envía un mensaje</li>
              <li><code>check-whatsapp-status</code> - Verifica el estado de WhatsApp</li>
            </ul>
          </div>

          <div className="help-documentation-code-block">
            <h3>Eventos de Chat</h3>
            <ul>
              <li><code>join-chat</code> - Unirse a un chat</li>
              <li><code>leave-chat</code> - Salir de un chat</li>
              <li><code>send-message</code> - Enviar mensaje al chat</li>
              <li><code>typing-status</code> - Estado de escritura</li>
            </ul>
          </div>
        </section>

        <section className="help-documentation-section">
          <h2>Autenticación y Seguridad</h2>
          <div className="help-documentation-code-block">
            <h3>Middleware de Autenticación</h3>
            <ul>
              <li>Verificación de tokens JWT</li>
              <li>Validación de permisos de usuario</li>
              <li>Separación entre clientes WhatsApp y usuarios normales</li>
              <li>Manejo de errores de autenticación</li>
            </ul>
          </div>
        </section>

        <section className="help-documentation-section">
          <h2>Integración con Redis</h2>
          <div className="help-documentation-code-block">
            <h3>Configuración de Redis</h3>
            <ul>
              <li>Adaptador Redis para Socket.IO</li>
              <li>Sincronización de eventos entre múltiples instancias</li>
              <li>Fallback a memoria si Redis no está disponible</li>
              <li>Configuración de base de datos Redis</li>
            </ul>
          </div>
        </section>

        <section className="help-documentation-section">
          <h2>Manejo de Estados</h2>
          <div className="help-documentation-code-block">
            <h3>Estados de WhatsApp</h3>
            <ul>
              <li><strong>disconnected</strong> - Desconectado</li>
              <li><strong>connecting</strong> - Conectando</li>
              <li><strong>connected</strong> - Conectado</li>
              <li><strong>pending</strong> - Esperando QR</li>
              <li><strong>error</strong> - Error de conexión</li>
            </ul>
          </div>
        </section>

        <section className="help-documentation-section">
          <h2>Funcionalidades de Chat</h2>
          <div className="help-documentation-code-block">
            <h3>Características del Chat</h3>
            <ul>
              <li>Mensajes en tiempo real</li>
              <li>Indicador de escritura</li>
              <li>Estado de conexión de usuarios</li>
              <li>Manejo de archivos y multimedia</li>
              <li>Notificaciones push</li>
            </ul>
          </div>
        </section>

        <section className="help-documentation-section">
          <h2>Manejo de Errores</h2>
          <div className="help-documentation-code-block">
            <h3>Tipos de Errores</h3>
            <ul>
              <li><strong>Errores de conexión:</strong> Problemas de red</li>
              <li><strong>Errores de autenticación:</strong> Tokens inválidos</li>
              <li><strong>Errores de WhatsApp:</strong> Problemas con la API de WhatsApp</li>
              <li><strong>Errores de Redis:</strong> Problemas de sincronización</li>
            </ul>
          </div>
        </section>

        <section className="help-documentation-section">
          <h2>Configuración</h2>
          <div className="help-documentation-code-block">
            <h3>Variables de Entorno</h3>
            <ul>
              <li><code>REDIS_ENABLED</code> - Habilita Redis</li>
              <li><code>REDIS_DB</code> - Base de datos Redis</li>
              <li><code>WHATSAPP_CONNECTION_TIMEOUT</code> - Timeout de conexión</li>
              <li><code>PORT</code> - Puerto del servidor</li>
            </ul>
          </div>
        </section>

        <section className="help-documentation-section">
          <h2>Archivos Relacionados</h2>
          <div className="help-documentation-related-files">
            <ul>
              <li><strong>index.js</strong> - Punto de entrada principal</li>
              <li><strong>app.js</strong> - Configuración de Express</li>
              <li><strong>WhatsAppClientManager</strong> - Cliente de WhatsApp</li>
              <li><strong>socketAuthMiddleware</strong> - Middleware de autenticación</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ViewBackendWhatsappManager;